import React, { useState, useRef, useEffect } from 'react';
import { Typography, Button, IconButton, Tooltip } from '@mui/material';
import { toast } from 'react-toastify';
import FeedbackIcon from '@mui/icons-material/Feedback';
import SendIcon from '@mui/icons-material/Send';
import CommentIcon from '@mui/icons-material/Comment';
import EmailIcon from '@mui/icons-material/Email';
import SubjectIcon from '@mui/icons-material/Subject';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useApi } from '../contexts/ApiContext';
import { useNavigate } from 'react-router-dom';
import { useWithTokenRefresh } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const Feedback = () => {
  const form = useRef<HTMLFormElement>(null);
  const { apiClient } = useApi();
  const navigate = useNavigate();
  const withTokenRefresh = useWithTokenRefresh();
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [charCount, setCharCount] = useState(0); // Initialisation à 0
  const MAX_CHARS = 1000;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [attachment, setAttachment] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string>('');
  const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 Mo en octets
  const { authState } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (authState.user) {
      setEmail(authState.user.email);
    }
  }, [authState.user]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setFileError('');

    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        setFileError(`File size exceeds the maximum limit of 25 MB`);
        setAttachment(null);
        // Reset the file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        setAttachment(file);
      }
    } else {
      setAttachment(null);
    }
  };

  const handleRemoveFile = () => {
    setAttachment(null);
    setFileError('');
    // Reset the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClearForm = () => {
    setSubject('');
    setMessage('');
    setCharCount(0);
    handleRemoveFile();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Simple validation
    if (!email || !subject || !message) {
      toast.error('Please fill in all fields');
      return;
    }

    if (!email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    // Validation de la longueur du message
    if (message.length > MAX_CHARS) {
      toast.error(`Your message exceeds the maximum limit of ${MAX_CHARS} characters`);
      return;
    }

    if (fileError) {
      toast.error(fileError);
      return;
    }

    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('email', email);
      formData.append('subject', subject);
      formData.append('message', message);
      if (attachment) {
        formData.append('attachment', attachment);
      }
      // Créer un objet pour l'API (pas FormData pour cette API)
      const feedbackData = formData;

      // Utiliser la fonction sendFeedbackEmail avec les données correctes
      const response = await withTokenRefresh(() => apiClient.sendFeedbackEmail(feedbackData));

      // Typer explicitement la réponse
      const typedResponse = response as { success?: boolean };
      if (typedResponse.success) {
        toast.success('Your feedback has been sent successfully');
        // Clear form after successful submission
        handleClearForm();
        // navigate to home
        navigate('/');
      } else {
        toast.error('An error occurred while sending your feedback');
      }
    } catch (error) {
      toast.error('An error occurred while sending your feedback');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 px-8 py-12">
      {/* Header avec titre et actions */}
      <div className="flex justify-between items-center mb-4">
        <Typography
          variant="h5"
          className="text-black"
          style={{
            fontWeight: 600,
            fontSize: '20px',
            lineHeight: '30px',
          }}
        >
          Send Feedback
        </Typography>
        <div className="flex items-center space-x-3">
          <Tooltip title="Clear form">
            <IconButton
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              onClick={handleClearForm}
              disabled={isSubmitting}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      {/* Texte informatif */}
      <div className="mb-6">
        <Typography
          variant="body2"
          className="text-gray-600"
          style={{
            fontSize: '14px',
            lineHeight: '20px',
          }}
        >
          We value your feedback! Please share your thoughts, suggestions, or report any issues
          you've encountered.
        </Typography>
      </div>

      {/* Formulaire principal */}
      <div className="bg-gray-100 rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="flex justify-between items-center p-4 border-b border-gray-200">
          <div className="flex items-center">
            <FeedbackIcon className="text-[#4766FF] mr-2" style={{ fontSize: 24 }} />
            <Typography variant="h6" className="font-medium text-gray-900">
              Feedback Form
            </Typography>
          </div>
        </div>

        <div className="p-6">
          <form ref={form} onSubmit={handleSubmit} className="space-y-6">
            {/* Email Field */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-2 flex items-center"
              >
                <EmailIcon fontSize="small" className="mr-2 text-gray-500" />
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#4766FF] bg-white text-sm"
                placeholder="Your email address"
                required
                disabled={isSubmitting}
              />
            </div>

            {/* Subject Field */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <label
                htmlFor="subject"
                className="block text-sm font-medium text-gray-700 mb-2 flex items-center"
              >
                <SubjectIcon fontSize="small" className="mr-2 text-gray-500" />
                Subject
              </label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#4766FF] bg-white text-sm"
                placeholder="Brief description of your feedback"
                required
                disabled={isSubmitting}
              />
            </div>

            {/* Message Field */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <label
                htmlFor="message"
                className="block text-sm font-medium text-gray-700 mb-2 flex items-center"
              >
                <CommentIcon fontSize="small" className="mr-2 text-gray-500" />
                Message
              </label>
              <textarea
                id="message"
                name="message"
                value={message}
                onChange={(e) => {
                  const input = e.target.value;
                  if (input.length <= MAX_CHARS) {
                    setMessage(input);
                    setCharCount(input.length);
                  }
                }}
                maxLength={MAX_CHARS}
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#4766FF] bg-white text-sm"
                placeholder="Describe your feedback, suggestion, or issue in detail..."
                required
                disabled={isSubmitting}
              />
              <div className="flex justify-between items-center mt-2">
                <Typography variant="caption" className="text-gray-500">
                  Please provide as much detail as possible
                </Typography>
                <Typography
                  variant="caption"
                  className={charCount > MAX_CHARS * 0.9 ? 'text-orange-500' : 'text-gray-500'}
                >
                  {charCount}/{MAX_CHARS} characters
                </Typography>
              </div>
            </div>

            {/* Attachment Field */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <label
                htmlFor="attachment"
                className="block text-sm font-medium text-gray-700 mb-2 flex items-center"
              >
                <AttachFileIcon fontSize="small" className="mr-2 text-gray-500" />
                Attachment (optional)
              </label>
              <div className="mt-1">
                <input
                  type="file"
                  id="attachment"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-[#4766FF] file:text-white hover:file:bg-blue-700 transition-colors"
                  disabled={isSubmitting}
                />
              </div>
              <Typography variant="caption" className="text-gray-500 mt-1 block">
                Maximum file size: 25 MB
              </Typography>
              {fileError && (
                <Typography variant="caption" className="text-red-600 mt-1 block">
                  {fileError}
                </Typography>
              )}
              {attachment && !fileError && (
                <div className="mt-3 flex items-center bg-gray-50 p-3 rounded-md border">
                  <AttachFileIcon fontSize="small" className="text-gray-500 mr-2" />
                  <div className="flex-grow">
                    <Typography variant="body2" className="text-gray-700 font-medium">
                      {attachment.name}
                    </Typography>
                    <Typography variant="caption" className="text-gray-500">
                      {(attachment.size / (1024 * 1024)).toFixed(2)} MB
                    </Typography>
                  </div>
                  <IconButton
                    onClick={handleRemoveFile}
                    disabled={isSubmitting}
                    size="small"
                    className="text-gray-500 hover:text-red-500"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <div className="flex justify-end pt-4">
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting || !!fileError || !email || !subject || !message}
                className="bg-[#4766FF] hover:bg-blue-700 text-white"
                style={{
                  fontSize: '14px',
                  lineHeight: '20px',
                  textTransform: 'none',
                  padding: '10px 24px',
                }}
                startIcon={isSubmitting ? null : <SendIcon />}
              >
                {isSubmitting ? 'Sending...' : 'Send Feedback'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Feedback;
